// src/components/forms/variants/popup/types.ts

import type { 
  BaseFormProps, 
  ContentConfig, 
  ThemeConfig,
  ImageConfig,
  FeatureListItemConfig,
  PrivacyConfig,
  BadgeConfig,
  SubtitleConfig
} from '~/components/forms/base/types'
import { siteConfig } from '~/lib/config/site'

/**
 * Popup-specific theme configuration
 */
export interface PopupThemeConfig extends ThemeConfig {
  /** Backdrop configuration */
  backdrop?: {
    /** Optional backdrop class name */
    className?: string
    /** Optional backdrop style */
    style?: React.CSSProperties
  }
}

/**
 * Configuration for the popup image
 */
export interface PopupImageConfig {
  /** Source URL of the image */
  src: string
  /** Alt text for accessibility */
  alt?: string
  /** Optional CSS classes */
  className?: string
  /** Optional overlay configuration */
  overlay?: {
    /** Whether to show overlay */
    enabled?: boolean
    /** Gradient or color for overlay */
    background?: string
    /** Opacity of overlay (0-1) */
    opacity?: number
  }
}

/**
 * Props for the PopupEmailCapture component
 */
export interface PopupEmailCaptureProps extends Omit<BaseFormProps, 'content' | 'theme'> {
  /** Whether the popup is open */
  isOpen: boolean
  /** Handler for closing the popup */
  onClose: () => void
  
  /** Content configuration extending base content */
  content: ContentConfig & {
    /** Optional image configuration */
    image?: ImageConfig
    /** Optional badge configuration */
    badge?: BadgeConfig
    /** Optional subtitle configuration */
    subtitle?: SubtitleConfig
    /** Optional feature list */
    features?: FeatureListItemConfig[]
    /** Optional privacy notice configuration */
    privacy?: PrivacyConfig
  }
  
  /** Theme configuration extending base theme */
  theme?: PopupThemeConfig
}

/**
 * Default preset configuration
 */
export const POPUP_PRESET: Partial<PopupEmailCaptureProps> = {
  theme: {
    variant: 'popup',
    layout: 'split',
    size: 'lg',
    theme: 'light',
    className: {
      container: 'max-w-xl',
      form: 'rounded-2xl bg-white dark:bg-gray-900 shadow-2xl'
    },
    backdrop: {
      className: 'bg-gray-900/50 backdrop-blur-sm dark:bg-gray-900/70'
    }
  },
  content: {
    input: {
      placeholder: 'Enter your work email'
    },
    button: {
      text: 'Subscribe',
      loadingText: 'Processing...'
    },
    messages: {
      success: 'Thanks for subscribing!',
      error: 'Please enter a valid email address.'
    },
    privacy: {
      enabled: true,
      text: 'By continuing, you agree to our',
      privacyLink: {
        text: 'Privacy Policy',
        url: `${siteConfig.baseLinks.privacy}`
      },
      termsLink: {
        text: 'Terms of Service',
        url: `${siteConfig.baseLinks.terms}`
      }
    }
  }
} as const 